/* ============================================
   TURBO THEME — CART JS
   ============================================ */

'use strict';

const TurboCart = {
  drawer: null,
  overlay: null,
  body: null,
  count: 0,

  init() {
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('#overlay');
    this.body = document.querySelector('.cart-drawer__body');

    document.querySelectorAll('[data-cart-open]').forEach(btn => {
      btn.addEventListener('click', () => this.open());
    });

    document.querySelector('[data-cart-close]')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });

    document.querySelectorAll('form[data-product-form]').forEach(form => {
      form.addEventListener('submit', e => this.handleAddToCart(e));
    });

    this.fetchCart();
    this.updateCount();
  },

  open() {
    this.fetchCart().then(() => {
      this.drawer?.classList.add('open');
      this.overlay?.classList.add('active');
      document.body.style.overflow = 'hidden';
    });
  },

  close() {
    this.drawer?.classList.remove('open');
    this.overlay?.classList.remove('active');
    document.body.style.overflow = '';
  },

  async fetchCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.renderCart(cart);
      this.count = cart.item_count;
      this.updateCount();
      return cart;
    } catch (err) {
      console.error('[Turbo Cart] fetchCart error:', err);
    }
  },

  renderCart(cart) {
    if (!this.body) return;

    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          <p style="font-weight:600;">Votre panier est vide</p>
          <p style="font-size:0.875rem;">Ajoutez des produits pour commencer</p>
          <a href="/collections/all" class="button" style="margin-top:8px;" onclick="TurboCart.close()">Voir la boutique</a>
        </div>
      `;
    } else {
      const itemsHtml = cart.items.map(item => this.renderItem(item)).join('');
      const footer = document.querySelector('.cart-drawer__footer');

      this.body.innerHTML = `<div class="cart-items">${itemsHtml}</div>`;

      if (footer) {
        const totalEl = footer.querySelector('[data-cart-total]');
        if (totalEl) {
          totalEl.textContent = Turbo.formatMoney(cart.total_price);
        }
      }
    }

    this.bindItemEvents();
  },

  renderItem(item) {
    const image = item.featured_image?.url
      ? `<img src="${item.featured_image.url}&width=160" alt="${item.featured_image.alt || item.title}" width="80" height="80" loading="lazy">`
      : `<div style="width:80px;height:80px;background:var(--color-border);border-radius:var(--radius-sm);"></div>`;

    const variantTitle = item.variant_title && item.variant_title !== 'Default Title' && item.variant_title !== 'Par défaut'
      ? `<span class="cart-item__variant">${item.variant_title}</span>` : '';

    return `
      <div class="cart-item" data-key="${item.key}">
        <a class="cart-item__image" href="${item.url}">${image}</a>
        <div class="cart-item__info">
          <a class="cart-item__title" href="${item.url}">${item.product_title}</a>
          ${variantTitle}
          <div class="cart-item__controls">
            <div class="product-info__qty" style="transform:scale(0.85);transform-origin:left;">
              <button class="qty-btn qty-btn--minus" data-action="decrease" data-key="${item.key}" aria-label="Decrease">−</button>
              <input class="qty-input" type="number" value="${item.quantity}" min="1" max="99" data-key="${item.key}" aria-label="Quantity">
              <button class="qty-btn qty-btn--plus" data-action="increase" data-key="${item.key}" aria-label="Increase">+</button>
            </div>
            <button class="cart-item__remove" data-action="remove" data-key="${item.key}" aria-label="Retirer l'article" style="margin-left:auto;color:var(--color-muted);font-size:0.8125rem;text-decoration:underline;background:none;border:none;cursor:pointer;">Retirer</button>
          </div>
          <span class="cart-item__price">${Turbo.formatMoney(item.final_line_price)}</span>
        </div>
      </div>
    `;
  },

  bindItemEvents() {
    const body = this.body;
    if (!body) return;

    body.querySelectorAll('[data-action="remove"]').forEach(btn => {
      btn.addEventListener('click', () => this.removeItem(btn.dataset.key));
    });

    body.querySelectorAll('[data-action="increase"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = body.querySelector(`input[data-key="${btn.dataset.key}"]`);
        if (input) this.updateItem(btn.dataset.key, parseInt(input.value) + 1);
      });
    });

    body.querySelectorAll('[data-action="decrease"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = body.querySelector(`input[data-key="${btn.dataset.key}"]`);
        if (input) {
          const newVal = parseInt(input.value) - 1;
          if (newVal < 1) this.removeItem(btn.dataset.key);
          else this.updateItem(btn.dataset.key, newVal);
        }
      });
    });

    body.querySelectorAll('input.qty-input').forEach(input => {
      input.addEventListener('change', Turbo.debounce(() => {
        let val = parseInt(input.value);
        if (isNaN(val) || val < 1) val = 1;
        this.updateItem(input.dataset.key, val);
      }, 500));
    });
  },

  async updateItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      this.renderCart(cart);
      this.count = cart.item_count;
      this.updateCount();
      Turbo.emit('cart:updated', { cart });
    } catch (err) {
      console.error('[Turbo Cart] updateItem error:', err);
    }
  },

  async removeItem(key) {
    await this.updateItem(key, 0);
  },

  async handleAddToCart(e) {
    e.preventDefault();
    const form = e.target;
    const btn = form.querySelector('[data-add-to-cart]');
    const originalText = btn?.textContent;

    if (btn) {
      btn.disabled = true;
      btn.classList.add('button--loading');
      btn.innerHTML = '<span class="loader"></span>';
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });

      if (!res.ok) throw new Error('Add to cart failed');

      await this.fetchCart();
      Turbo.emit('cart:item-added');

      if (window.Shopify?.theme && document.querySelector('[data-cart-type="drawer"]')) {
        this.open();
      } else {
        window.TurboNotification?.show('Produit ajouté au panier !');
      }
    } catch (err) {
      console.error('[Turbo Cart] Add to cart error:', err);
      window.TurboNotification?.show('Une erreur est survenue. Veuillez réessayer.', 'error');
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('button--loading');
        btn.textContent = originalText;
      }
    }
  },

  updateCount() {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = this.count;
      el.style.display = this.count > 0 ? 'flex' : 'none';
    });
  }
};

document.addEventListener('DOMContentLoaded', () => TurboCart.init());
window.TurboCart = TurboCart;
